import {Box, Button, Grid, MenuItem, Paper, TextField, Typography, Accordion, AccordionSummary, AccordionDetails} from '@mui/material'
import axios from 'axios'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { MoonLoader } from 'react-spinners'
import { toast } from 'react-toastify'
import DynamicForm from '../../../../reusable-components/forms/DynamicForm'
import FormWrapper from '../../../../reusable-components/Wrapper/Wrapper'
import { EDIT_BEHAVIOUR_NOTE_FORM_INPUTS } from '../BEHAVIOUR_CONSTANTS'
import { RootState } from '../../../../../setup/redux/RootReducer'
import DescriptionModal from '../../../../reusable-components/description-modal/descriptionmodal'
import NavigateButton from '../../../../reusable-components/description-modal/navigatebutton'
import FrequencyDateRangePicker from '../../../../reusable-components/NoteType/NoteType'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const selectTimeZone = (state: RootState) => state.timeZone.timeZone

export default function EditBehaviourNote() {
  const params: {id: string} = useParams()
  const history = useHistory()
  const [isFormLoading, setIsFormLoading] = useState(true)
  const [formData, setFormData] = useState<any>({})
  const authUser = useSelector((state: {auth: any}) => state.auth)
  const [uiElements, setUiElements] = useState([])
  const [clientId, setClientId] = useState('')
  const [selectedClientId, setSelectedClientId] = useState<{ label: string; value: string } | null>(null)
  const [clients, setClients] = useState([])
  const timeZone = useSelector(selectTimeZone)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [description, setDescription] = useState('')
  const [pendingSubmit, setPendingSubmit] = useState(false)
  const [submittedFormData, setSubmittedFormData] = useState(null)
  const [behaviorFormData, setBehaviorFormData] = useState([])
  
  useEffect(() => {
    const loadData = async () => {
      setIsFormLoading(true)
      try {
        
        // First, fetch the progress note data
        const res = await axios.get(`/progressNotes/getById/${params.id}`)
        const data = res?.data?.result
        // Timezone-converted dates
        const formattedStartDate = dayjs.utc(data.startDate).tz(timeZone, true)
        const formattedEndDate = dayjs.utc(data.endDate).tz(timeZone, true)
        const formattedNextAppointment = dayjs.utc(data.nextAppointment).tz(timeZone, true)
        // Set basic form data
        const prevFormData = {
          startDate:formattedStartDate,
          endDate:formattedEndDate,
          timeIn:data.timeIn || '',
          timeOut:data.timeOut || '',
          noteType:data.noteType || '',
          dangerTo: data?.dangerTo || [],
          status: data?.status || '',
          responseToTx: data?.responseToTx || '',
          appearance: data?.appearance || '',
          judgment: data?.judgment || '',
          insights: data?.insights || '',
          speech: data?.speech || '',
          mood: data?.mood || '',
          behaviour: data?.behaviour || '',
          thoughtContent: data?.thoughtContent || '',
          thoughtProcess: data?.thoughtProcess || '',
          orientation: data?.orientation || '',
          affect: data?.affect || '',
          therapeuticInterventions: data?.therapeuticInterventions || [],
          location: data?.historyOfIllness?.location || '',
          duration: data?.historyOfIllness?.duration || '',
          timing: data?.historyOfIllness?.timing || '',
          associatedSymptoms: data?.historyOfIllness?.associatedSymptoms || '',
          medicalHx: data?.historyOfIllness?.medicalHx || '',
          goalsObjectives: data?.goalsObjectives || '',
          evidenceOfResponse: data?.evidenceOfResponse || '',
          interventionStudies: data?.interventionStudies || '',
          sessionContent: data?.sessionContent || '',
          futureTreatment: data?.futureTreatment || '',
          assignedHomework: data?.assignedHomework || '',
          nextAppointment: formattedNextAppointment,        }
  
        // Load behavior mapping data if it exists
        if (data?.behaviorMapping) {
          const behaviorsData = data.behaviorMapping.map((behavior) => {
            return {
              isEdit: false,
              id: behavior._id,
              targetBehavior: behavior.targetBehavior,
              targetBehaviorId: behavior.targetBehaviorId,
              targetRating: behavior.targetRating || '',
              targetDescription: behavior.targetDescription || '',
              replacementBehavior: behavior.replacementBehavior.map((replacement) => ({
                _id: replacement._id,
                name: replacement.name,
                type: replacement.type,
                rating: replacement.rating || '',
                description: replacement.description || ''
              })),
            }
          })
          setBehaviorFormData(behaviorsData)
        }
  
        setFormData(prevFormData)
        setClientId(data?.clientId)

        const response = await axios.get('/clients/getClientNames')
        const { data: clientsList } = response?.data
  
        const updatedClients = clientsList.map((client) => ({
          label: client.fullName,
          value: client._id
        }))
  
        const selectedClient = updatedClients.find((client) => client.value === data?.clientId)
  
        const updatedFormData = EDIT_BEHAVIOUR_NOTE_FORM_INPUTS.map((item) => {
          if (item.name === 'clientId') {
            return {
              ...item,
              options: updatedClients,
              disabled: true,
              value: selectedClient.value,
            }
          }
          return item
        })
  
        setSelectedClientId(selectedClient)
        setUiElements(updatedFormData)
      } catch (error) {
        console.error('Error loading progress note data:', error)
      } finally {
        setIsFormLoading(false)
      }
    }
  
    loadData()
  }, [])

    const handleSaveDescription = (desc) => {
    setDescription(desc)
    setIsModalOpen(false)
    setPendingSubmit(false)

    if (submittedFormData) {
      handleSubmitForm(submittedFormData)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setPendingSubmit(false)
    setSubmittedFormData(null)
  }

 const handleSubmitForm = async (formValues) => {
    if (!description.trim() && !isModalOpen) {
      setSubmittedFormData(formValues)
      setIsModalOpen(true)
      setPendingSubmit(true)
      return
    }

    const formPayload = submittedFormData || formValues

    const clientIdValue = selectedClientId?.value
    const clientFullName = selectedClientId?.label  
    const userId = authUser.user._id
    const requiredFields = [
      'dangerTo',
      'status',
      'responseToTx',
      'appearance',
      'judgment',
      'insights',
      'speech',
      'mood',
      'behaviour',
      'thoughtContent',
      'thoughtProcess',
      'orientation',
      'affect',
      'nextAppointment',
      'location',
      'duration',
      'timing',
      'associatedSymptoms',
      'medicalHx',
    ]

    for (const field of requiredFields) {
      if (!formPayload[field] || (typeof formPayload[field] === 'string' && !formPayload[field].trim())) {
        return toast.error(`"${field}" is required`)
      }
    }
    const applyCurrentTime = (date, now) =>
  dayjs(date)
    .hour(now.hour())
    .minute(now.minute())
    .second(0)
    .millisecond(0)

const now = dayjs()

const formattedNextAppt = applyCurrentTime(formPayload['nextAppointment'], now)
  .tz(timeZone, true)
  .toDate()

    const currentDate = dayjs()
    if (dayjs(formattedNextAppt).isBefore(currentDate, 'day')) {
      toast.error('Appointment cannot be in the past.')
      return
    }
    const dateSigned = dayjs().format('YYYY-MM-DD')
    const noteId = params.id
    setIsFormLoading(true)
    try {
      const payload = {
        clientId: clientIdValue,
        programId: formPayload['programId'],
        noteType: formData['noteType'],
        startDate: formData['startDate'],
        endDate:formData['endDate'],
        timeIn: formData['timeIn'] ? dayjs(formData['timeIn'], 'hh:mm A').format('hh:mm A') : '',
        timeOut: formData['timeOut'] ? dayjs(formData['timeOut'], 'hh:mm A').format('hh:mm A') : '',
        dangerTo: formPayload['dangerTo'],
        status: formPayload['status'],
        responseToTx: formPayload['responseToTx'],
        appearance: formPayload['appearance'],
        judgment: formPayload['judgment'],
        insights: formPayload['insights'],
        speech: formPayload['speech'],
        mood: formPayload['mood'],
        behaviour: formPayload['behaviour'],
        thoughtContent: formPayload['thoughtContent'],
        thoughtProcess: formPayload['thoughtProcess'],
        orientation: formPayload['orientation'],
        affect: formPayload['affect'],
        therapeuticInterventions: formPayload['therapeuticInterventions'],
        historyOfIllness: {
          location: formPayload.location,
          duration: formPayload.duration,
          timing: formPayload.timing,
          associatedSymptoms: formPayload.associatedSymptoms,
          medicalHx: formPayload.medicalHx,
        },
        goalsObjectives: formPayload['goalsObjectives'],
        evidenceOfResponse: formPayload['evidenceOfResponse'],
        interventionStudies: formPayload['interventionStudies'],
        sessionContent: formPayload['sessionContent'],
        futureTreatment: formPayload['futureTreatment'],
        assignedHomework: formPayload['assignedHomework'],
        nextAppointment: formattedNextAppt,
        therapistSignature: userId,
        behaviorMapping: behaviorFormData,
        isPdfGenerated: false,
      }

      const res = await axios.put(`/progressNotes/update/${noteId}`, payload)
      const {message, success} = res?.data

      if (!success) {
        return toast.error(message)
      }

      toast.success(message)
      history.push('/notes/behavior-notes/list')
    } catch (e) {
      toast.error(e?.response?.data?.error || `Couldn't Add Note`)
      console.error('Error adding behaviour note:', e)
    } finally{
      setIsFormLoading(false)
      setSubmittedFormData(null)
    }
  }
  const buttons = [
    {label: 'Cancel', variant: 'outlined', type: 'button'},
    {label: 'Save', variant: 'contained', type: 'submit'},
  ]

  const handleChange = ({ target: { value, name } }, index) => {
    const updatedBehaviorFormData = [...behaviorFormData]
    updatedBehaviorFormData[index][name] = value
    setBehaviorFormData(updatedBehaviorFormData)
  }

  const handleReplacementChange = ({ target: { value, name } }, replacementIndex, index) => {
    const updatedBehaviorFormData = [...behaviorFormData]
    updatedBehaviorFormData[index].replacementBehavior[replacementIndex][name] = value
    setBehaviorFormData(updatedBehaviorFormData)
  }
  return (
      <FormWrapper
        title='Behavior Note'
        content={
          <>  
          {isFormLoading ? (
                <div
                  className='overlay d-flex align-middle justify-content-center align-middle'
                  style={{marginTop: '100px'}}
                >
                  <MoonLoader color='#9db2fc' size={60} loading={isFormLoading} />
                </div>
              ) : (
                <>
                <span style={{ display: 'flex', justifyContent: 'end', marginTop: '-40px' }}>
                  <NavigateButton
                  label='View Edit History'
                  clientId={clientId}
                  section='behavior notes section'
                  />
                  </span>
                  
                <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                  marginLeft: '1rem',
                }}
                  color='primary'
                >
                  Note Type
                </Typography> 
                <FrequencyDateRangePicker
                  isEntry = {false}
                  isTemplate = {true}
                  isFullForm = {true}
                  isEdit = {true}
                  formData={formData}
                  setFormData={setFormData}
              />

              {formData?.noteType === "DAILY" && behaviorFormData.map((behavior, index) => (
            <Accordion key={behavior.id} sx={{ mb: 2, mt: 2, border: 'none', width: '97%' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: '#f5f5f5',
                  borderBottom: 'none',
                  '&:hover': {
                    backgroundColor: '#eeeeee',
                  },
                }}
              >
                <Typography variant='h6' sx={{ fontWeight: 600, color: '#3f51b5' }}>
                  Behavior {index + 1}: {behavior.targetBehavior}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={8} sm={8} md={8}>
                    <Box
                      sx={{
                        mb: 1,
                        p: 1,
                        border: '1px solid #ccc',
                        borderRadius: 2,
                        backgroundColor: '#f7f7f7',
                      }}
                    >
                      <Grid container alignItems="center">
                        <Grid item>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: '#3f51b5',
                            }}
                          >
                            Target Behavior
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography
                            variant="body1"
                            sx={{
                              color: '#333',
                              fontStyle: 'italic',
                              p: 1,
                              borderRadius: 1,
                              marginLeft: "7rem"
                            }}
                          >
                            {behavior.targetBehavior}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4}>
                    <TextField
                      fullWidth
                      select
                      label='Rating'
                      name='targetRating'
                      variant='outlined'
                      value={behavior.targetRating || ''}
                      onChange={(e) => handleChange(e, index)}
                    >
                      <MenuItem value="0">0</MenuItem>
                      <MenuItem value="1">1</MenuItem>
                      <MenuItem value="2">2</MenuItem>
                      <MenuItem value="3">3</MenuItem>
                      <MenuItem value="4">4</MenuItem>
                      <MenuItem value="5">5</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label='Description'
                      name='targetDescription'
                      variant='outlined'
                      multiline
                      rows={3}
                      value={behavior.targetDescription}
                      onChange={(e) => handleChange(e, index)}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {behavior.replacementBehavior.map((replacement, replacementIndex) => (
                      <Box key={replacementIndex} sx={{ mb: 3 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={8} sm={8} md={8}>
                            <Box
                              sx={{
                                mb: 1,
                                p: 1,
                                border: '1px solid #ccc',
                                borderRadius: 2,
                                backgroundColor: '#f7f7f7',
                              }}
                            >
                              <Grid container alignItems="center">
                                <Grid item>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      color: '#3f51b5',
                                    }}
                                  >
                                    Replacement Behavior
                                  </Typography>
                                </Grid>
                                <Grid item>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      color: '#333',
                                      fontStyle: 'italic',
                                      p: 1,
                                      borderRadius: 1,
                                      marginLeft: "4rem"
                                    }}
                                  >
                                    {replacement.name}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Grid>
                          <Grid item xs={4} sm={4} md={4}>
                            <TextField
                              fullWidth
                              select
                              label='Rating'
                              name='rating'
                              variant='outlined'
                              value={replacement.rating || ''}
                              onChange={(e) => handleReplacementChange(e, replacementIndex, index)}
                            >
                              <MenuItem value="0">0</MenuItem>
                              <MenuItem value="1">1</MenuItem>
                              <MenuItem value="2">2</MenuItem>
                              <MenuItem value="3">3</MenuItem>
                              <MenuItem value="4">4</MenuItem>
                              <MenuItem value="5">5</MenuItem>
                            </TextField>
                          </Grid>
                        </Grid>
                        <TextField
                          fullWidth
                          label='Description'
                          name='description'
                          variant='outlined'
                          multiline
                          rows={3}
                          value={replacement.description}
                          onChange={(e) => handleReplacementChange(e, replacementIndex, index)}
                        />
                      </Box>
                    ))}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
              ))}

            <DynamicForm
              fields={uiElements}
              data={formData}
              onSubmit={handleSubmitForm}
              buttons={buttons}
            />
            <DescriptionModal
          open={isModalOpen}
          onClose={handleCloseModal}
          clientID={clientId}
          onSave={handleSaveDescription}
          section='behavior notes section'
        />
            </>
          )}
          </>
        }
        />
  )
}
